import moment from 'moment'
import {
	AGE_WHEN_PASSPORT_CAN_BE_RECEIVED,
	DATE_FORMAT,
	DATE_FORMAT_FOR_BACKEND,
	DaysOfWeek,
	MILLISECOND_IN_DAY,
	PASSPORT_CHANGE_AGE_20,
	PASSPORT_CHANGE_AGE_45,
	PASSPORT_VALIDITY_PERIOD_DAYS,
	YEAR_FORMAT,
} from 'Src/constants/Global'
import { yearsLabel, daysMoreLabel, validateDate } from 'Helpers'
import {
	isStartDateMoreThanMaxHour,
	TYPE_ORGANIZATION,
	ValidateMessages,
	RejectDescr,
	ActiveLead,
	getCurrentFormatMoscowDate,
} from 'Pages/ibro'
import {
	cyrillicChars,
	domainRegExp,
	emailRegExp,
	ERROR_CODE,
	floatNum,
	hasDigitsReg,
	hasLengthReg,
	hasSpecialReg,
	ipAdressRegExp,
	latinChars,
	LengthValidatorPayload,
	MessageErrorType,
	messageTranslator,
	nonDigits,
	okvedRegExp,
	onlyCyrillicCharsWithHyphen,
	passportCodeRegExp,
	passportNumberRegExp,
	passportSeriesRegExp,
	posNameAllowedSymbols,
	removeHtmlTags,
	SearchTextValidatorPayload,
	ValidateAgePayload,
	validateIfNumberExistsPayload,
	ValidateLessDatePayload,
	ValidateLessMoreDatePayload,
	ValidateMaxLengthPayload,
	ValidateMoreDatePayload,
	ValidatePassportIntervalPayload,
	ValidationDate,
	VALIDATOR_ERROR_MESSAGE,
	ValidatorOptions,
	ValidatorResult,
	validateIfDateExistInPayloadType,
	urlWithHttpRegExp,
	IkzOptions,
	RegexOptions,
	LengthOptions,
	EqualTextOptions,
	IncludesTextOptions,
	isNumInStr,
	isStrLowerCase,
	isStrUpperCase,
	HouseAddressOptions,
	house_reg,
	apartment_reg,
	numberLatinicCyrillic,
	cyrillicCharsWithSymbols,
	startsWithAlphanumericCyrillic,
	building_reg,
	block_type_reg,
	hasLowerCaseReg,
	hasUpperCaseReg,
	hasAllowedSymbolReg,
} from 'Modules'
import { LEAD_BEARER } from 'Pages/lead'

export const isNumValidator = (value: string) => {
	const validate = new ValidatorResult()
	if (nonDigits.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.only_numbers')
	} else {
		validate.result = true
	}
	return validate
}

export const floatNumValidator = (value: string) => {
	const validate = new ValidatorResult()
	if (!floatNum.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.only_numbers')
	} else {
		validate.result = true
	}
	return validate
}

export const lengthValidator = (
	value: string,
	options: ValidatorOptions<LengthValidatorPayload>
) => {
	const validate = new ValidatorResult()
	const { length } = options.payload
	if (typeof value !== 'string' || value?.length !== length) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options.messageError as string) ?? 'common.strict_length_symbols',
			[length.toString()]
		)
	} else {
		validate.result = true
	}
	return validate
}

export const emailValidator = (value: string) => {
	const validate = new ValidatorResult()
	let resEmail = value
	if (typeof resEmail !== 'string') {
		resEmail = ''
	}
	if (!emailRegExp.test(resEmail)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.invalid_email')
	} else {
		validate.result = true
	}
	return validate
}

export const validateUrl = (value: string) => {
	const validate = new ValidatorResult()
	if (!domainRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.invalid_url')
	} else {
		validate.result = true
	}
	return validate
}

const validateQuotes = (value: string) => {
	const { length } = value
	const matched = value.match(/"/g)
	if (matched && matched.length % 2 !== 0) {
		return messageTranslator('common.company_name_unclosed_quotes')
	}
	if (value[0] === '"' && length > 0 && value[length - 1] === '"') {
		return messageTranslator('common.quotes_wrapping_not_allowed')
	}
	return null
}

export const validatePasswordGivenBy = (value: string) => {
	const validate = new ValidatorResult()
	if (!startsWithAlphanumericCyrillic.test(value)) {
		validate.error.message = messageTranslator('common.only_cyrillic')
	}
	if (validate.error.message) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateCyrillicName = (value: string) => {
	const validate = new ValidatorResult()
	if (!cyrillicCharsWithSymbols.test(value)) {
		validate.error.message = messageTranslator('common.only_cyrillic')
	}
	if (validate.error.message) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateLatinicName = (value: string) => {
	const validate = new ValidatorResult()
	if (cyrillicChars.test(value)) {
		validate.error.message = messageTranslator('common.cyrillic_not_allowed')
	} else {
		validate.error.message = validateQuotes(value) ?? validate.error.message
	}
	if (validate.error.message) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateCyrillicsWithHyphen = (value: string) => {
	const validate = new ValidatorResult()
	if (onlyCyrillicCharsWithHyphen.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.only_cyrillic')
	} else {
		validate.result = true
	}
	return validate
}

export const validatePOSName = (value: string) => {
	const validate = new ValidatorResult()
	if (posNameAllowedSymbols.test(value) || value.length < 2 || value.length > 24) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.invalid_pos_name')
	} else {
		validate.result = true
	}
	return validate
}

const phoneRegExp = /^[+][(]?[7][3489][0-9]{2}[)]?[-\s.]?[0-9]{7}$/
export const validatePhone = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (!phoneRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.invalid_phone'
		)
	} else {
		validate.result = true
	}
	return validate
}

const phoneWithMaskRegExp = /^\+7\s?\(\d{3}\)\s?\d{3}(-\d{2}){2}$/
export const validatePhoneWithMask = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (!phoneWithMaskRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.invalid_phone'
		)
	} else {
		validate.result = true
	}
	return validate
}

export type ValidatePositiveNumberPayload = {
	min?: number
	max?: number
	isFloat?: boolean
}

export const validatePositiveNumber = (
	value: string,
	options?: ValidatorOptions<ValidatePositiveNumberPayload>
) => {
	const validate = new ValidatorResult()
	const { min, max, isFloat } = options?.payload || {}
	const isNum = floatNumValidator(value)
	if (!isFloat && !isNum.result) {
		return isNum
	}
	const numValue = Number(value)
	if (min && numValue < min) {
		return {
			result: false,
			error: {
				code: ERROR_CODE.MIN_COUNT.replace(':data', min.toString()),
				message:
					(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MIN] ??
					messageTranslator('common.min_value', [min.toString()]),
			},
		}
	}
	if (max && numValue > max) {
		return {
			result: false,
			error: {
				code: ERROR_CODE.MAX_COUNT.replace(':data', max.toString()),
				message:
					(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MAX] ??
					messageTranslator('common.max_value', [max.toString()]),
			},
		}
	}

	if (numValue <= 0) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.positive_number')
	} else {
		validate.result = true
	}
	return validate
}

export const validateMoreDate = (
	value: string,
	options: ValidatorOptions<ValidateMoreDatePayload>
) => {
	const validate = new ValidatorResult()

	const { min } = options.payload
	const date = moment(value)
	const minDate = moment(min)

	if (value && min && date.format('YYYY-MM-DD') > minDate.format('YYYY-MM-DD')) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.invalid_date_period'
		)
	} else validate.result = true
	return validate
}

export const validateLessDate = (
	value: string,
	options: ValidatorOptions<ValidateLessDatePayload>
) => {
	const validate = new ValidatorResult()
	const { max } = options.payload
	const date = moment(value)
	const maxDate = moment(max)

	if (value && max && date.format('YYYY-MM-DD') < maxDate.format('YYYY-MM-DD')) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.invalid_date_period'
		)
	} else validate.result = true
	return validate
}

export const validateLessMoreDate = (
	value: ValidationDate,
	options: ValidatorOptions<ValidateLessMoreDatePayload>
) => {
	const validate = new ValidatorResult()
	const { from, to, daysCount } = options?.payload || {}
	const date = moment(value)
	const dateFrom = moment(from)
	const dateTo = moment(to)
	const dateValue = moment(value)

	if (value && from && date.format('YYYY-MM-DD') < dateFrom.format('YYYY-MM-DD')) {
		validate.error.message = messageTranslator(
			(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MORE] ??
				'common.invalid_date_period'
		)
	} else if (
		value &&
		from &&
		daysCount &&
		(Date.parse(dateValue.format()) - Date.parse(dateFrom.format())) / MILLISECOND_IN_DAY >
			daysCount
	) {
		validate.error.message = messageTranslator('common.no_more_than_days', [
			daysCount.toString(),
			daysMoreLabel(daysCount),
		])
	} else if (value && to && date.format('YYYY-MM-DD') > dateTo.format('YYYY-MM-DD')) {
		validate.error.message = messageTranslator(
			(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.LESS] ??
				'common.invalid_date_period'
		)
	} else validate.result = true

	if (!validate.result) validate.error.code = ERROR_CODE.BAD_FORMAT

	return validate
}

export const validateIpAddress = (value: string) => {
	const validate = new ValidatorResult()

	if (!ipAdressRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.invalid_ip_address')
	} else {
		validate.result = true
	}
	return validate
}

export const validateMomentDate = (value: string, options: ValidatorOptions) => {
	const validate = new ValidatorResult()
	// todo check format & fix
	const format_for_validation = options?.format || DATE_FORMAT_FOR_BACKEND
	if (!validateDate(value, format_for_validation)) {
		// todo
		const errorCode = moment(value, DATE_FORMAT).invalidAt()
		switch (errorCode) {
			case 1:
				validate.error.message = messageTranslator('common.date_invalid_month')
				break
			case 2:
				validate.error.message = messageTranslator('common.date_invalid_day')
				break
			default:
				validate.error.message = messageTranslator('common.date_invalid')
				break
		}
	} else {
		validate.result = true
	}
	if (!validate.result) validate.error.code = ERROR_CODE.BAD_FORMAT
	return validate
}

export const validateMaxLength = (
	value: string,
	options: ValidatorOptions<ValidateMaxLengthPayload>
) => {
	const validate = new ValidatorResult()
	const { max } = options.payload
	if (value && value.length > max) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.max_length',
			[String(max)]
		)
	} else {
		validate.result = true
	}
	return validate
}

export const searchText = (
	value: string,
	options: ValidatorOptions<SearchTextValidatorPayload>
) => {
	const validate = new ValidatorResult()
	const { text, isRegister, isOnlyBegin } = options.payload
	const prepareValue = isRegister ? (value || '').toLowerCase() : value
	const findIndex = prepareValue.indexOf(text)
	if (isOnlyBegin && findIndex !== -1) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options.messageError as string) ?? 'common.text_not_found',
			[text]
		)
	} else {
		validate.result = true
	}
	return validate
}

export const clearError = () => {
	const validate = new ValidatorResult()
	validate.result = true
	return validate
}

export const validateAge = (
	value: ValidationDate,
	options: ValidatorOptions<ValidateAgePayload>
) => {
	const validate = new ValidatorResult()
	const { age = 18 } = options?.payload || {}

	if (moment(value).isValid() && moment(new Date()).diff(moment(value), 'years') < age) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.age_less_than',
			[`${age} ${yearsLabel(age)}`]
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateMinMaxAge = (
	value: ValidationDate,
	options: ValidatorOptions<ValidateAgePayload>
) => {
	const validate = new ValidatorResult()
	const { age = 18, maxAge = 100 } = options?.payload || {}
	const isMinAgeDifference = moment(value).isAfter(
		moment(new Date()).subtract(age, 'years').startOf('day')
	)
	const isMaxAgeDifference = moment(value).isBefore(
		moment(new Date()).subtract(maxAge, 'years').startOf('day')
	)

	if (isMinAgeDifference) {
		validate.error.message = messageTranslator(
			(options?.messageError as string)?.[VALIDATOR_ERROR_MESSAGE.LESS] ?? 'common.age_less_than',
			[`${age} ${yearsLabel(age)}`]
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else if (isMaxAgeDifference) {
		validate.error.message = messageTranslator(
			(options?.messageError as string)?.[VALIDATOR_ERROR_MESSAGE.MORE] ?? 'common.age_less_than',
			[`${maxAge} ${yearsLabel(maxAge)}`]
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validate14yearsPassportInterval = (
	value: ValidationDate,
	options: ValidatorOptions<ValidatePassportIntervalPayload>
) => {
	const validate = new ValidatorResult()
	const { birthDate } = options.payload
	if (
		moment(value).isValid() &&
		moment(birthDate).isValid() &&
		moment(value).diff(moment(birthDate), 'years') < AGE_WHEN_PASSPORT_CAN_BE_RECEIVED
	) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.must_be_14_years_interval'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validatePassportIssueDate = (
	value: ValidationDate,
	options: ValidatorOptions<ValidatePassportIntervalPayload>
) => {
	const validate = new ValidatorResult()
	const { birthDate } = options.payload
	const isValid45Years =
		moment().diff(moment(birthDate).add(PASSPORT_CHANGE_AGE_45, 'years'), 'days') >
			PASSPORT_VALIDITY_PERIOD_DAYS &&
		moment(value).isBefore(moment(birthDate).add(PASSPORT_CHANGE_AGE_45, 'years'))
	const isValid20Years =
		moment().diff(moment(birthDate).add(PASSPORT_CHANGE_AGE_20, 'years'), 'days') >
			PASSPORT_VALIDITY_PERIOD_DAYS &&
		moment(value).isBefore(moment(birthDate).add(PASSPORT_CHANGE_AGE_20, 'years'))
	if (
		moment(value).isValid() &&
		moment(birthDate).isValid() &&
		(isValid45Years || isValid20Years)
	) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.passport_given_date.past_due'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}

	return validate
}

export const validatePassportSeries = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (value && !passportSeriesRegExp.test(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.passport_series_invalid'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validatePassportNumber = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (value && !passportNumberRegExp.test(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.passport_number_invalid'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateOkveds = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (value && !okvedRegExp.test(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.okved_numbers'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateIfNumberExists = (
	value: string,
	options?: ValidatorOptions<validateIfNumberExistsPayload>
) => {
	const validate = new ValidatorResult()
	const { array } = options.payload
	if (value && array.includes(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.is_required'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateIfValueIsTrue = (value: string, options: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (!value) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.is_required'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validatePassportCode = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (value && !passportCodeRegExp.test(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.passport_code_invalid'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateIsEqualPassword = (
	value: string,
	options: ValidatorOptions<{ equialTo: string }>
) => {
	const validate = new ValidatorResult()
	const { equialTo } = options.payload
	if (value !== equialTo) {
		validate.error.message = messageTranslator('common.passwords_do_not_match')
	} else {
		validate.result = true
	}
	return validate
}

export const validateDependentField =
	(
		dependentField: string,
		errorText?: string
	): ((rule: any, value: string, callback: any) => Promise<any>) =>
	async (rule, value: string, callback) => {
		if (!value && dependentField) callback(errorText)
	}

export const validateIfDateExistInPayload = (
	value: ValidationDate,
	options?: ValidatorOptions<validateIfDateExistInPayloadType>
) => {
	const validate = new ValidatorResult()
	const { array } = options.payload || {}
	const date = moment(value)

	const weekday = date.weekday()

	const isWeekend = weekday === DaysOfWeek.SATURDAY || weekday === DaysOfWeek.SUNDAY
	const currentYear = (array ?? []).find((calendar) => {
		return String(calendar.year) === date.format(YEAR_FORMAT)
	})

	const day_off = currentYear?.non_working_days.includes(date.format(DATE_FORMAT_FOR_BACKEND))
	const work_day = currentYear?.working_days.includes(date.format(DATE_FORMAT_FOR_BACKEND))

	if (value && day_off) {
		validate.error.message = messageTranslator(
			String(options.messageError) || 'broker.more_next_day'
		)
	} else if (value && isWeekend && !work_day) {
		validate.error.message = messageTranslator(
			String(options.messageError) || 'broker.more_next_day'
		)
	} else {
		validate.result = true
	}

	return validate
}

export const validateUrlWithHttp = (value: string) => {
	const validate = new ValidatorResult()
	if (!urlWithHttpRegExp.test(value)) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.invalid_url')
	} else {
		validate.result = true
	}
	return validate
}

type MaxHour = {
	max: number
}
export const validateStartDateMoreThanMaxHour = (
	value: string,
	options: ValidatorOptions<MaxHour>
) => {
	const validate = new ValidatorResult()

	if (value && isStartDateMoreThanMaxHour(value, options.payload.max)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'broker.more_next_day'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateCodeIkz = (value: string, options: ValidatorOptions<IkzOptions>) => {
	const validate = new ValidatorResult()
	const { inn, kpp } = options.payload
	const conditions = [
		value?.length === 36,
		+value[0] >= 1 && +value[0] <= 3,
		value.slice(3, 13) === inn,
		value.slice(13, 22) === kpp,
	]

	if (!inn || !kpp) {
		validate.error.message = messageTranslator('broker.ikz_inn_kpp_error')
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else if (!conditions.every(Boolean)) {
		validate.error.message = messageTranslator('broker.ikz_error')
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateByRegex = (value: string, options: ValidatorOptions<RegexOptions>) => {
	const validate = new ValidatorResult()
	const { regex } = options.payload

	if (!regex.test(value)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'broker.invalid_url_address'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateLength = (value: string, options: ValidatorOptions<LengthOptions>) => {
	const validate = new ValidatorResult()

	const { min, max } = options?.payload || {}
	const valLength = String(value).length

	if (min && valLength < min) {
		return {
			result: false,
			error: {
				code: ERROR_CODE.MIN_COUNT.replace(':data', min.toString()),
				message:
					(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MIN] ??
					messageTranslator('common.min_value_length', [min.toString()]),
			},
		}
	}
	if (max && valLength > max) {
		return {
			result: false,
			error: {
				code: ERROR_CODE.MAX_COUNT.replace(':data', max.toString()),
				message:
					(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MAX] ??
					messageTranslator('common.max_value_length', [max.toString()]),
			},
		}
	}

	validate.result = true

	return validate
}

export const isEqualText = (value: string, options: ValidatorOptions<EqualTextOptions>) => {
	const validate = new ValidatorResult()
	const { register, text } = options.payload
	if ((register && value !== text) || value?.toLowerCase() !== text?.toLowerCase()) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.not_match'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const isSameFioForIp = (value: string, options: ValidatorOptions<IncludesTextOptions>) => {
	const validate = new ValidatorResult()
	if (
		options.payload?.orgType === TYPE_ORGANIZATION.IP &&
		!options.payload?.text?.toLowerCase()?.endsWith(value?.toLowerCase())
	) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'fio.not match company name'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateLotNumber = (value: string) => {
	const validate = new ValidatorResult()
	const lotNumber = Number(value)
	if (lotNumber <= 0 || lotNumber > 2147483648) {
		validate.error.message = messageTranslator('broker.not_between_0_2147483648')
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const isValidPasswordValidator = (value: string) => {
	const validate = new ValidatorResult()
	if (
		!latinChars.test(value) ||
		!isNumInStr.test(value) ||
		value?.length <= 8 ||
		!(value.match(isStrLowerCase) && value.match(isStrUpperCase))
	) {
		return {
			result: false,
			error: {
				code: ERROR_CODE.BAD_FORMAT,
				message: messageTranslator('common.no_valid_password'),
			},
		}
	}
	validate.result = true

	return validate
}
export const checkHasServerError = (value: string, options: ValidatorOptions<{ text: string }>) => {
	const validate = new ValidatorResult()
	if (options.payload.text) {
		validate.error.message = options.payload.text
		validate.error.code = ERROR_CODE.BAD_FORMAT
	}

	return validate
}

export const validateMaxLengthWithoutLink = (
	value: string,
	options: ValidatorOptions<ValidateMaxLengthPayload>
) => {
	const validate = new ValidatorResult()
	const { max } = options.payload
	if (value && removeHtmlTags(value).length > max) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'common.max_length',
			[String(max)]
		)
	} else {
		validate.result = true
	}
	return validate
}

export const isValidCharacters = (
	value: string,
	options: ValidatorOptions<{ allowSymbols?: string }>
) => {
	const validate = new ValidatorResult()
	const { allowSymbols = '' } = options?.payload || {}
	const findInvalidCharacter = (val = '') =>
		val.split('').find((w) => !new RegExp(`[${allowSymbols}а-яёa-zА-ЯЁA-Z0-9 ]`).test(w))
	const invalidCharacter = findInvalidCharacter(value)

	if (invalidCharacter) {
		validate.error.message = messageTranslator('broker.invalid_character', [invalidCharacter])
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}

	return validate
}

export const validateLessMoreEqualsDate = (
	value: ValidationDate,
	options: ValidatorOptions<ValidateLessMoreDatePayload>
) => {
	const validate = new ValidatorResult()
	const { from, to, daysCount } = options?.payload || {}
	const date = moment(value)
	const dateFrom = moment(from)
	const dateTo = moment(to)
	const dateValue = moment(value)

	if (value && from && date.format('YYYY-MM-DD') < dateFrom.format('YYYY-MM-DD')) {
		validate.error.message = messageTranslator(
			(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.MORE] ??
				'common.invalid_date_period'
		)
	} else if (
		value &&
		from &&
		daysCount &&
		(Date.parse(dateValue.format()) - Date.parse(dateFrom.format())) / MILLISECOND_IN_DAY >
			daysCount
	) {
		validate.error.message = messageTranslator('common.no_more_than_days', [
			daysCount.toString(),
			daysMoreLabel(daysCount),
		])
	} else if (value && to && date.format('YYYY-MM-DD') > dateTo.format('YYYY-MM-DD')) {
		validate.error.message = messageTranslator(
			(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.LESS] ??
				'common.invalid_date_period'
		)
	} else if (value && from && date.format('YYYY-MM-DD') === dateFrom.format('YYYY-MM-DD')) {
		validate.error.message = messageTranslator(
			(options?.messageError as MessageErrorType)?.[VALIDATOR_ERROR_MESSAGE.EQUALS] ??
				'common.invalid_date_period'
		)
	} else validate.result = true

	if (!validate.result) validate.error.code = ERROR_CODE.BAD_FORMAT

	return validate
}

export const validateHouseAddress = (
	value: string,
	options: ValidatorOptions<{ address: HouseAddressOptions[] }>
) => {
	const validate = new ValidatorResult()
	const { address = [] } = options?.payload || {}
	const current = address.find((item) => item.value === value)
	const hasHouse = !!current?.value.match(house_reg)
	const hasApartment = !!current?.value.match(apartment_reg)
	const hasBuilding = !!current?.value.match(building_reg)
	const hasBlock = !!current?.value.match(block_type_reg)
	const addressValue = hasHouse || hasBuilding || hasBlock

	if (value && !addressValue) {
		validate.error.message = messageTranslator('common.house_street_in_address')
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else if (value && hasApartment) {
		validate.error.message = messageTranslator('common.no_apartment_in_address')
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateCommission = (
	value: string,
	options: ValidatorOptions<{ commissionReject: boolean }>
) => {
	const validate = new ValidatorResult()
	if (options?.payload?.commissionReject) {
		validate.error.message = ValidateMessages[RejectDescr.NPA]
	} else {
		validate.result = true
	}
	return validate
}

export const isNumberLatinicCyrillic = (value: string) => {
	const validate = new ValidatorResult()
	if (numberLatinicCyrillic.test(value) || value.length < 2 || value.length > 24) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = messageTranslator('common.number_latinic_cyrillic')
	} else {
		validate.result = true
	}
	return validate
}

export const passwordValidator = (value: string) => {
	const validate = new ValidatorResult()
	if (
		!hasSpecialReg.test(value) ||
		!hasLengthReg.test(value) ||
		!hasLowerCaseReg.test(value) ||
		!hasUpperCaseReg.test(value) ||
		!hasAllowedSymbolReg.test(value) ||
		!hasDigitsReg.test(value)
	) {
		validate.error.code = ERROR_CODE.PASSWORD_BAD_FORMAT
	} else {
		validate.result = true
	}
	return validate
}

export const validateActiveLead = (
	_,
	options: ValidatorOptions<{ activeLead: ActiveLead | undefined }>
) => {
	const validate = new ValidatorResult()
	const { activeLead } = options.payload

	const isNotHaveBearer = ![LEAD_BEARER.SMS, LEAD_BEARER.DIGITAL, LEAD_BEARER.PAPER].includes(
		activeLead?.bearer
	)

	if (isNotHaveBearer) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = 'Отсутствует согласие на обработку ПДн'
		return validate
	}

	if (activeLead?.require_renew) {
		validate.error.code = ERROR_CODE.BAD_FORMAT
		validate.error.message = 'Необходимо обновить согласие ПДн'
		return validate
	}

	validate.result = true
	return validate
}

const regex = /["]/g
export const validateMustHaveQuote = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	if (value && value.match(regex)) {
		validate.result = true
	} else validate.error.message = messageTranslator(options?.messageError as string, [value])

	return validate
}

const latin = /[A-z]/
export const validateQuotesOrgName = (value: string, options?: ValidatorOptions) => {
	const validate = new ValidatorResult()
	const matched = value.match(/"/g)
	if (latin.test(value)) {
		validate.error.message = messageTranslator('common.only_cyrillic')
	} else if (matched && matched.length % 2 !== 0) {
		validate.error.message = messageTranslator(options?.messageError as string, [value])
	} else {
		validate.result = true
	}

	if (!validate.result) validate.error.code = ERROR_CODE.BAD_FORMAT

	return validate
}

export const validateCyrillicOrLatinQuotes = (
	value: string,
	options: ValidatorOptions<{ isLatin: boolean }>
) => {
	const validate = new ValidatorResult()
	const { isLatin } = options.payload
	const reg = options.payload.isLatin ? /[а-яёА-ЯЁ]/ : /[a-zA-Z]/
	if (reg.test(value)) {
		validate.error.message = isLatin
			? 'Нельзя использовать кириллицу'
			: 'Нельзя использовать латиницу'
	} else if (value.match(regex)?.length === 1) {
		validate.error.message = 'Кавычка должна быть закрыта'
	} else validate.result = true

	return validate
}

export const validateNearestWeekend = (
	value: string,
	options?: ValidatorOptions<{ maxHour: number; weekends: string[] }>
) => {
	const validate = new ValidatorResult()
	const { maxHour, weekends } = options.payload

	const currentDate = getCurrentFormatMoscowDate()
	const moreThenMaxHour = isStartDateMoreThanMaxHour(currentDate, maxHour)
	const selectedDate = moment(value).format('YYYY-MM-DD')

	if (moreThenMaxHour && weekends.includes(selectedDate)) {
		validate.error.message = messageTranslator(
			(options?.messageError as string) ?? 'broker.more_next_day'
		)
		validate.error.code = ERROR_CODE.BAD_FORMAT
	} else {
		validate.result = true
	}

	return validate
}
